/**
 * 模拟走查 — answer the draft questionnaire and see what a submit would grant.
 *
 * All logic is in utils/simulate.ts; this only renders it. Answers live in page state and
 * nowhere else: nothing is stored, nothing is sent.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Input,
  Link,
  RadioGroup,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react';

import type { AnswerOption, QuestionnaireDefinition, QuestionnaireNode } from '../../types';
import { programColorOf } from '../../utils/programCategory';
import { ruleSummary, summaryLookupOf } from '../../utils/ruleSummary';
import { isAnswered, SIM_SCENARIOS, type SimAnswer, type SimAnswers, type Simulation } from '../../utils/simulate';
import './SidePanel.css';

export interface SimulationPanelProps {
  definition: QuestionnaireDefinition;
  sim: Simulation;
  answers: SimAnswers;
  onAnswers: (next: SimAnswers) => void;
  onSelectNode: (id: string) => void;
}

const STATUS: Record<string, { type: 'success' | 'stopped' | 'pending' | 'error'; text: string }> = {
  hit: { type: 'success', text: '命中' },
  miss: { type: 'stopped', text: '未命中' },
  pending: { type: 'pending', text: '待定' },
  error: { type: 'error', text: '报错' },
};

const optLabel = (o: AnswerOption) => o.optionText.cn || o.optionText.en || o.value;

function AnswerInput({
  node,
  value,
  disabled,
  onChange,
}: {
  node: QuestionnaireNode;
  value: SimAnswer | undefined;
  disabled: boolean;
  onChange: (v: SimAnswer | undefined) => void;
}) {
  const opts = node.answerOptions ?? [];
  if (opts.length && node.multiSelect) {
    const cur = Array.isArray(value) ? value : [];
    return (
      <SpaceBetween size="xxs">
        {opts.map((o) => (
          <Checkbox
            key={o.value}
            checked={cur.includes(o.value)}
            disabled={disabled}
            onChange={({ detail }) =>
              onChange(detail.checked ? [...cur, o.value] : cur.filter((x) => x !== o.value))
            }
          >
            <code>{o.value}</code> {optLabel(o)}
          </Checkbox>
        ))}
      </SpaceBetween>
    );
  }
  if (opts.length) {
    return (
      <RadioGroup
        value={typeof value === 'string' ? value : null}
        onChange={({ detail }) => onChange(detail.value)}
        items={opts.map((o) => ({ value: o.value, label: `${o.value} · ${optLabel(o)}`, disabled }))}
      />
    );
  }
  const numeric = node.questionType === 'INPUT_NUMBER';
  return (
    <Input
      type={numeric ? 'number' : 'text'}
      value={value === undefined ? '' : String(value)}
      disabled={disabled}
      placeholder={numeric ? (node.unit ?? '') : ''}
      onChange={({ detail }) =>
        onChange(detail.value === '' ? undefined : numeric ? Number(detail.value) : detail.value)
      }
    />
  );
}

export default function SimulationPanel({ definition, sim, answers, onAnswers, onSelectNode }: SimulationPanelProps) {
  const byId = new Map(definition.nodes.map((n) => [n.nodeId, n]));
  const look = summaryLookupOf(definition);
  const purposeE = answers.Q_0_2 === 'e';

  return (
    <SpaceBetween size="m">
      <div>
        <Box variant="awsui-key-label">示例场景</Box>
        <div className="cpa-sim__scenarios">
          {SIM_SCENARIOS.map((s) => (
            <Button key={s.id} onClick={() => onAnswers({ ...s.answers })}>
              {s.name}
            </Button>
          ))}
          <Button variant="link" onClick={() => onAnswers({})}>
            清空
          </Button>
        </div>
      </div>

      <div>
        <Box variant="h4">授予的项目（{sim.granted.length}）</Box>
        <Box variant="small" color="text-body-secondary">
          按授予顺序（= 后端 programList）。语义同 QuestionnaireProcessor：沿路径逐题、题内按规则顺序。
        </Box>
        {sim.granted.length === 0 ? (
          <Box color="text-status-inactive">暂无</Box>
        ) : (
          <ol className="cpa-sim__granted">
            {sim.granted.map((g) => (
              <li key={g.program}>
                <span className="cpa-sim__prog" style={{ background: programColorOf(g.program) }}>
                  {g.program}
                </span>{' '}
                <Link onFollow={() => onSelectNode(`${g.nodeId}::rule::${g.index}`)}>
                  ← {g.nodeId}#{g.index + 1}
                </Link>
              </li>
            ))}
          </ol>
        )}
      </div>

      {sim.end === 'done' && <StatusIndicator type="success">已到终点 → Review / Submit</StatusIndicator>}
      {sim.end === 'stuck' && <StatusIndicator type="error">没有命中的出边（blocked / no-branch）</StatusIndicator>}
      {sim.end === 'cycle' && (
        <StatusIndicator type="error">
          走回了已经问过的题（{sim.path[sim.path.length - 1]} 之后）—— 草稿里有环，问卷永远到不了终点
        </StatusIndicator>
      )}
      {purposeE && (
        <Alert type="warning">
          现网选 e 不进问卷，直接弹邮件引导框；草稿里它是 Q1.5.1 一道文本题，文档没有「出口」类型（TODO P1-3）。
        </Alert>
      )}
      {sim.orphaned.length > 0 && (
        <Alert type="info">
          {sim.orphaned.length} 个答案不在当前路径上（{sim.orphaned.join('、')}），不参与求值。
        </Alert>
      )}

      <div>
        <Box variant="h4">问卷路径（{sim.path.length} 题）</Box>
        <SpaceBetween size="s">
          {sim.path.map((id) => {
            const n = byId.get(id)!;
            const q = n.qualifiedNodeId ?? n.nodeId;
            const auto = sim.autoAnswered[id];
            const autoDone = isAnswered(auto);
            return (
              <div key={id} className={`cpa-sim__q ${id === sim.current ? 'cpa-sim__q--current' : ''}`}>
                <div className="cpa-sim__qh">
                  <Link onFollow={() => onSelectNode(id)}>{id}</Link>
                  {autoDone && <StatusIndicator type="info">现网自动作答（新前端丢失 · P1-1）</StatusIndicator>}
                  {(n.programRules ?? []).length > 0 && (
                    <span className="cpa-sim__count">{(n.programRules ?? []).length} 条资格规则</span>
                  )}
                </div>
                <div className="cpa-sim__qt">{n.questionText.cn || n.questionText.en}</div>
                <AnswerInput
                  node={n}
                  value={autoDone ? auto : answers[q]}
                  disabled={autoDone}
                  onChange={(v) => onAnswers({ ...answers, [q]: v })}
                />
              </div>
            );
          })}
        </SpaceBetween>
      </div>

      <div>
        <Box variant="h4">本路径上逐条资格规则</Box>
        {sim.rules.length === 0 ? (
          <Box color="text-status-inactive">路径上还没有带规则的题</Box>
        ) : (
          <SpaceBetween size="xs">
            {sim.rules.map((r) => {
              const rule = byId.get(r.nodeId)!.programRules![r.index];
              const st = STATUS[r.result.status];
              return (
                <div key={`${r.nodeId}#${r.index}`} className="cpa-sim__rule">
                  <div className="cpa-sim__qh">
                    <Link onFollow={() => onSelectNode(`${r.nodeId}::rule::${r.index}`)}>
                      {r.nodeId}#{r.index + 1}
                    </Link>
                    <StatusIndicator type={st.type}>{st.text}</StatusIndicator>
                    {r.result.status === 'pending' && (
                      <Box variant="small">等待 {r.result.waitingFor.join(', ')}</Box>
                    )}
                    {r.result.status === 'error' && <Box variant="small">{r.result.message}</Box>}
                  </div>
                  <div className="cpa-sim__qt">{ruleSummary(rule.expression, look)}</div>
                  <Box variant="small" color="text-body-secondary">
                    → {rule.values.join('、')}
                  </Box>
                </div>
              );
            })}
          </SpaceBetween>
        )}
      </div>
      {Object.keys(answers).some((q) => isAnswered(answers[q])) && (
        <Box variant="small" color="text-body-secondary">
          答案只在当前页面内存里，不保存、不发送。
        </Box>
      )}
    </SpaceBetween>
  );
}
